import { useEffect, useState } from "react";
import { X } from "lucide-react";

import { useAttemptApplication } from "@/api/application-api";
import { CandidateApplicationForm } from "@/components/tests/CandidateApplicationForm";
import { CaseReviewer } from "./CaseReviewer";
import { RecordingsStepper } from "./RecordingsStepper";
import { useAttemptReview } from "@/api/tests-api";

/**
 * An earlier sitting, opened rather than merely listed.
 *
 * History used to be a list of verdicts — one line per panel member per
 * round. With nothing scored there is no verdict to list, and what a
 * reviewer actually wants from an earlier round is the same two things
 * this screen shows for the current one: what the candidate said, and
 * how they dictated.
 *
 * READ-ONLY THROUGHOUT, including the per-case comments. An earlier
 * round is a record somebody may already have acted on; the place to
 * write is the round being reviewed now.
 *
 * The application comes from the ATTEMPT, not the candidate: the row is
 * unique per candidate and the next sitting edits it, so reading the
 * live one here would show answers written months after this sitting
 * and say nothing about the difference.
 */
export function PastRoundReviewer({
  attemptId,
  title,
  onClose,
}: {
  attemptId: string;
  candidateId: string;
  title: string;
  onClose: () => void;
}) {
  const application = useAttemptApplication(attemptId);
  const review = useAttemptReview(attemptId);
  const [viewerAt, setViewerAt] = useState<number | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      data-cy="past-round-reviewer"
      className="fixed inset-0 z-[70] flex flex-col bg-background text-foreground"
    >
      <header className="flex items-center justify-between gap-4 bg-background-topbar px-6 py-3 text-foreground-on-chrome shadow-theme-sm">
        <div className="flex min-w-0 items-center gap-4">
          <span className="truncate text-sm font-medium">{title}</span>
          <span className="shrink-0 rounded-full bg-background-card px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-foreground-muted">
            An earlier sitting
          </span>
        </div>
        <button
          type="button"
          data-cy="btn-close-past-round"
          onClick={onClose}
          className="flex items-center gap-1.5 rounded-md border border-border bg-background-card px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-table-row-hover"
        >
          <X aria-hidden className="h-3.5 w-3.5" />
          Back
        </button>
      </header>

      <div className="flex-1 overflow-auto px-8 py-6">
        <div className="mx-auto w-full max-w-[1240px]">
          <p className="sk-help">
            Read-only. This is what the candidate said and recorded at the
            time — comments belong to the sitting being reviewed now.
          </p>

          <h3 className="mt-6 text-[11px] font-semibold uppercase tracking-[0.12em] text-accent-hover">
            Their application, as it stood then
          </h3>
          <section className="mt-2 rounded-xl border border-border bg-background-card p-4">
            {application.data ? (
              <CandidateApplicationForm answers={application.data} />
            ) : application.isLoading ? (
              <p className="sk-help">Loading…</p>
            ) : (
              <p className="sk-help">
                No application was recorded for this sitting.
              </p>
            )}
          </section>

          <h3 className="mt-6 text-[11px] font-semibold uppercase tracking-[0.12em] text-accent-hover">
            Their dictations
          </h3>
          <div className="mt-2">
            {/* The case viewer this opens is mounted with canEdit
                false, so an earlier round can be read and listened to
                but not annotated. */}
            <RecordingsStepper attemptId={attemptId} onOpenCase={setViewerAt} />
          </div>
        </div>
      </div>
      {viewerAt !== null && (
        <CaseReviewer
          answers={review.data?.answers ?? []}
          initialIndex={viewerAt}
          attemptId={attemptId}
          canEdit={false}
          onClose={() => setViewerAt(null)}
        />
      )}
    </div>
  );
}
